import { ApiError } from './client';
import type {
  ChatAgent, ChatbotLead, ChatConversationListResponse, ChatConversationDetail, ChatConversationStatus, ChatRolloutResponse, ChatSettings,
} from '../types';

async function send<T = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data: T | { error?: string };
  try {
    data = await res.json();
  } catch {
    data = {} as T;
  }
  if (!res.ok) {
    const detail = (data as { error?: string }).error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError((data as { error?: string }).error ?? `${res.status}`, res.status);
  }
  return data as T;
}

export const chatApi = {
  listLeads: (af: string) =>
    get<{ leads: ChatbotLead[]; storeEnabled: boolean }>(
      `/api/chat/leads?af=${encodeURIComponent(af)}`),
  isEnabled: (af: string) =>
    get<{ enabled: boolean }>(`/api/chat/enabled?af=${encodeURIComponent(af)}`),
  getSettings: (af: string) =>
    get<{ settings: ChatSettings }>(`/api/chat/settings?af=${encodeURIComponent(af)}`),

  saveSettings: (af: string, settings: Partial<ChatSettings>) =>
    send<{ settings: ChatSettings }>(`/api/chat/settings?af=${encodeURIComponent(af)}`, 'PUT', settings),

  listAgents: (af: string) =>
    get<{ agents: ChatAgent[] }>(`/api/chat/agents?af=${encodeURIComponent(af)}`),

  upsertAgent: (
    af: string,
    agent: {
      user_id: number;
      role_sales: boolean;
      role_recruiting: boolean;
      display_name?: string;
      cell_e164?: string;
    },
  ) => send<{ agent: ChatAgent }>(`/api/chat/agents?af=${encodeURIComponent(af)}`, 'POST', agent),

  setInitialFace: (af: string, id: number, on: boolean) =>
    send<{ agents: ChatAgent[] }>(
      `/api/chat/agents/${id}/initial-face?af=${encodeURIComponent(af)}`, 'PUT', { on },
    ),
  removeAgent: (af: string, id: number) =>
    send<{ ok: true }>(`/api/chat/agents/${id}?af=${encodeURIComponent(af)}`, 'DELETE'),

  /** Multipart — the photo is shown to site visitors, so it is served from an
   *  unguessable public path rather than from behind the session cookie. */
  uploadPhoto: async (af: string, id: number, file: File) => {
    const fd = new FormData();
    fd.append('photo', file);
    const res = await fetch(`/api/chat/agents/${id}/photo?af=${encodeURIComponent(af)}`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new ApiError((data as { error?: string }).error ?? `${res.status}`, res.status);
    }
    return data as { photo_url: string };
  },

  rollout: () => get<ChatRolloutResponse>('/api/chat/rollout'),

  // ── cell verification (before any text goes to an agent) ──────────
  startCellVerification: (af: string, agentId: number) =>
    send<{ ok: true; expires_in_minutes: number }>(`/api/chat/agents/${agentId}/verify-cell/start?af=${encodeURIComponent(af)}`, 'POST', {}),
  confirmCellVerification: (af: string, agentId: number, code: string) =>
    send<{ ok: true }>(`/api/chat/agents/${agentId}/verify-cell/confirm?af=${encodeURIComponent(af)}`, 'POST', { code }),

  // ── inbox ──────────────────────────────────────────────────────────
  listConversations: (af: string, status?: ChatConversationStatus) =>
    get<ChatConversationListResponse>(
      `/api/chat/conversations?af=${encodeURIComponent(af)}${status ? `&status=${status}` : ''}`,
    ),

  getConversation: (af: string, id: string, sinceSeq?: number) =>
    get<ChatConversationDetail>(
      `/api/chat/conversations/${encodeURIComponent(id)}?af=${encodeURIComponent(af)}${sinceSeq ? `&since_seq=${sinceSeq}` : ''}`,
    ),

  /** An agent turn; also takes the conversation over if nobody holds it. */
  reply: (af: string, id: string, body: string) =>
    send<{ ok: true; seq: number }>(`/api/chat/conversations/${encodeURIComponent(id)}/messages?af=${encodeURIComponent(af)}`, 'POST', { body }),

  takeOver: (af: string, id: string) =>
    send<{ ok: true }>(`/api/chat/conversations/${encodeURIComponent(id)}/assign?af=${encodeURIComponent(af)}`, 'POST', {}),

  closeConversation: (af: string, id: string) =>
    send<{ ok: true }>(`/api/chat/conversations/${encodeURIComponent(id)}/close?af=${encodeURIComponent(af)}`, 'POST', {}),
};
